import { toValidDate } from "../../../utils/date";
import { normalizeToSlotStart } from "../../../utils/publicationDistribution";
import { stableValue } from "../sessionDefaults";
import { areDatesEqualToMinute, deepEqual } from "./compare";
import { computeSessionPublicationChangeSet } from "./dirtyState";
import type { SchedulingHistory } from "./history";
import { publicationPinKey } from "./pins";
import { isEditablePersistedPub, isImmutablePub } from "./status";
import type { LocalSessionState, PublicationModel, SchedulingSession } from "./types";

const equal = (a: unknown, b: unknown) => deepEqual(stableValue(a), stableValue(b));

/** A local field wins while editable; untouched fields follow the server.
 * Arrays are a single field, so an edited list is never merged by position. */
function mergeEditedFields(base: unknown, local: unknown, remote: unknown): unknown {
  if (equal(base, local)) return remote;
  const record = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);
  if (!record(local) || !record(remote) || (base != null && !record(base))) return local;
  const original = record(base) ? base : {};
  const merged: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(original), ...Object.keys(local), ...Object.keys(remote)])) {
    if (!(key in local) && key in original) continue; // Locally removed field.
    if (!(key in remote) && equal(original[key], local[key])) continue;
    merged[key] = mergeEditedFields(original[key], local[key], remote[key]);
  }
  return merged;
}

function mergePublication(base: PublicationModel, local: PublicationModel, remote: PublicationModel): PublicationModel {
  if (!isEditablePersistedPub(remote) || base.accountId !== remote.accountId || base.provider !== remote.provider) return remote;
  return {
    ...remote,
    scheduledAt: equal(base.scheduledAt, local.scheduledAt) ? remote.scheduledAt : local.scheduledAt,
    postInfo: mergeEditedFields(base.postInfo, local.postInfo, remote.postInfo) as PublicationModel["postInfo"],
  };
}

/** An Auto post the planner still moves (more than a day out, with a margin for
 * its next run): a manual post may take its hour, and the planner moves it off. */
export const isMovableAutoPost = (publication: Pick<PublicationModel, "autoFolderId" | "status" | "scheduledAt">, now = Date.now()) =>
  Boolean(publication.autoFolderId) && publication.status === "scheduled"
  && new Date(publication.scheduledAt).getTime() > now + MOVABLE_AUTO_POST_MS;
const MOVABLE_AUTO_POST_MS = 26 * 60 * 60 * 1000;

/** A Save that changes an Auto post's time or status makes it the user's own
 * (save_publication_batch clears auto_folder_id), so a staged change already
 * shows it as theirs. A row the baseline doesn't hold keeps its marker. */
export const stagedAutoFolderId = (
  publication: Pick<PublicationModel, "autoFolderId" | "status" | "scheduledAt">,
  saved: Pick<PublicationModel, "status" | "scheduledAt"> | undefined,
): string | null => {
  if (!publication.autoFolderId || !saved) return publication.autoFolderId ?? null;
  return saved.status === publication.status &&
    areDatesEqualToMinute(toValidDate(saved.scheduledAt), toValidDate(publication.scheduledAt))
    ? publication.autoFolderId : null;
};

/** The same publication with its staged Auto post marker; unchanged rows keep their identity. */
export const withStagedAutoFolderId = <T extends Pick<PublicationModel, "autoFolderId" | "status" | "scheduledAt">>(
  publication: T,
  saved: Pick<PublicationModel, "status" | "scheduledAt"> | undefined,
): T => {
  const autoFolderId = stagedAutoFolderId(publication, saved);
  return autoFolderId === (publication.autoFolderId ?? null) ? publication : { ...publication, autoFolderId };
};

export function buildCrossProjectOccupied(publications: readonly PublicationModel[], videoIds: ReadonlySet<string>, now = Date.now()) {
  const occupied = new Map<string, Set<number>>();
  for (const publication of publications) {
    if ((publication.projectId && videoIds.has(publication.projectId)) ||
      !["scheduled", "published", "publishing", "verifying"].includes(publication.status) ||
      isMovableAutoPost(publication, now)) continue;
    const date = toValidDate(publication.scheduledAt);
    if (!date) continue;
    const slots = occupied.get(publication.accountId) ?? new Set<number>();
    slots.add(normalizeToSlotStart(date).getTime());
    occupied.set(publication.accountId, slots);
  }
  return occupied;
}

/** Update a snapshot without materializing new drafts or moving staged times.
 * Every undo/redo snapshot receives the same server facts, so history can only
 * undo local work and cannot resurrect a deleted or published post. */
export function reconcileSchedulingSession(session: SchedulingSession, publications: readonly PublicationModel[]): SchedulingSession {
  const scope = new Set([...session.baseline.orderedVideoIds, ...session.entryState.orderedVideoIds, ...session.local.orderedVideoIds]);
  const latest = publications.filter(p => p.projectId && scope.has(p.projectId));
  const beforeById = new Map(session.baseline.publications.map(p => [p.id, p]));
  const latestById = new Map(latest.map(p => [p.id, p]));
  const latestByVideo = new Map<string, PublicationModel[]>();
  for (const publication of latest) {
    const rows = latestByVideo.get(publication.projectId!) ?? [];
    rows.push(publication);
    latestByVideo.set(publication.projectId!, rows);
  }

  const syncLocal = (state: LocalSessionState, preserveEdits: boolean): LocalSessionState => {
    const orderedVideoIds = [...state.orderedVideoIds];
    const perVideoState = { ...state.perVideoState };
    const videoAccountOverrides = { ...state.videoAccountOverrides };
    for (const videoId of scope) {
      // Entry/reset snapshots must not acquire shorts added later by the user.
      if (!preserveEdits && !state.orderedVideoIds.includes(videoId) && !state.perVideoState[videoId]) continue;
      const beforeRows = state.perVideoState[videoId]?.localPublications ?? [];
      const remoteRows = latestByVideo.get(videoId) ?? [];
      const rows: PublicationModel[] = [];
      const seen = new Set<string>();
      for (const local of beforeRows) {
        const base = beforeById.get(local.id);
        const remote = latestById.get(local.id);
        if (base) {
          if (!remote || remote.projectId !== videoId) continue;
          rows.push(preserveEdits ? mergePublication(base, local, remote) : remote);
          seen.add(remote.id);
        } else if (remote?.projectId === videoId) {
          // A previously uncertain save may have committed this draft's ID.
          rows.push(remote);
          seen.add(remote.id);
        } else {
          // A new saved row now covers this generated draft's destination.
          const saved = remoteRows.find(p => p.accountId === local.accountId && !beforeById.has(p.id));
          if (!saved) rows.push(local);
          else if (!seen.has(saved.id)) {
            const openingDraft = session.entryState.perVideoState[videoId]?.localPublications.find(p => p.id === local.id);
            rows.push(preserveEdits && openingDraft ? mergePublication(openingDraft, local, saved) : saved);
            seen.add(saved.id);
          }
        }
      }
      for (const remote of remoteRows) {
        if (seen.has(remote.id)) continue;
        // Preserve an explicit local deletion while the row is still editable.
        // New rows and newly immutable rows must be visible and cannot be deleted.
        if (!preserveEdits || !beforeById.has(remote.id) || isImmutablePub(remote)) rows.push(remote);
      }
      if (!orderedVideoIds.includes(videoId) && rows.length > 0) {
        const originalIndex = session.baseline.orderedVideoIds.indexOf(videoId);
        orderedVideoIds.splice(originalIndex < 0 ? orderedVideoIds.length : Math.min(originalIndex, orderedVideoIds.length), 0, videoId);
      }
      if (orderedVideoIds.includes(videoId)) perVideoState[videoId] = { localPublications: rows };
      // An exclusion can no longer hide history. Clear that obsolete intent.
      for (const remote of rows.filter(isImmutablePub)) {
        if (videoAccountOverrides[videoId]?.[remote.provider]?.kind !== "exclude") continue;
        videoAccountOverrides[videoId] = { ...videoAccountOverrides[videoId] };
        delete videoAccountOverrides[videoId][remote.provider];
        if (Object.keys(videoAccountOverrides[videoId]).length === 0) delete videoAccountOverrides[videoId];
      }
    }
    return { ...state, orderedVideoIds, perVideoState, videoAccountOverrides };
  };
  const local = syncLocal(session.local, true);
  const entryState = syncLocal(session.entryState, false);
  const baselineLocal = syncLocal(session.baseline, false);
  const cleanPins = (keys: Set<string>, state: LocalSessionState) => {
    const allowed = new Set(Object.entries(state.perVideoState).flatMap(([id, vs]) => vs.localPublications
      .filter(p => !isImmutablePub(p)).flatMap(p => [`${id}:${p.accountId}`, publicationPinKey(id, p.id)])));
    return new Set([...keys].filter(key => allowed.has(key)));
  };
  const crossProjectOccupied = buildCrossProjectOccupied(publications, scope);
  const next: SchedulingSession = {
    ...session,
    baseline: { ...baselineLocal, publications: latest },
    existingPublicationIds: new Set(latest.map(p => p.id)),
    local,
    entryState,
    customDatetimeKeys: cleanPins(session.customDatetimeKeys, local),
    entryCustomDatetimeKeys: cleanPins(session.entryCustomDatetimeKeys, entryState),
    crossProjectOccupied,
    preStartFromSnapshot: session.preStartFromSnapshot ? syncLocal(session.preStartFromSnapshot, true) : undefined,
  };
  // Polls with the same data must not create history steps or invalidate previews.
  const comparable = (value: SchedulingSession) => ({
    baseline: { ...value.baseline, publications: [...value.baseline.publications].sort((a, b) => a.id.localeCompare(b.id)) },
    local: value.local, entryState: value.entryState, preStartFromSnapshot: value.preStartFromSnapshot,
    pins: [...value.customDatetimeKeys].sort(), entryPins: [...value.entryCustomDatetimeKeys].sort(),
    occupied: [...value.crossProjectOccupied].map(([id, times]) => [id, [...times].sort()]).sort(),
  });
  return equal(comparable(session), comparable(next)) ? session : next;
}

export function reconcileSchedulingHistory(history: SchedulingHistory, publications: readonly PublicationModel[]): SchedulingHistory {
  const snapshots = new Map<SchedulingSession, SchedulingSession>();
  const reconcile = (session: SchedulingSession) => {
    if (!snapshots.has(session)) snapshots.set(session, reconcileSchedulingSession(session, publications));
    return snapshots.get(session)!;
  };
  const session = reconcile(history.session);
  const past = history.past.map(reconcile);
  const future = history.future.map(reconcile);
  if (session === history.session && past.every((s, i) => s === history.past[i]) && future.every((s, i) => s === history.future[i])) return history;
  const sameIntent = (a: SchedulingSession, b: SchedulingSession) => {
    const intent = (s: SchedulingSession) => ({
      local: s.local, config: s.config, cadences: [...s.accountCadences],
      pins: [...s.customDatetimeKeys].sort(), baseline: s.baseline,
      preStartFromSnapshot: s.preStartFromSnapshot,
    });
    return equal(intent(a), intent(b));
  };
  // A publishing update can erase an entire edit. Don't leave an enabled
  // Undo/Redo button whose next step would change nothing.
  const compact = (states: SchedulingSession[], start: SchedulingSession) => {
    let previous = start;
    return states.filter(state => {
      if (sameIntent(previous, state)) return false;
      previous = state;
      return true;
    });
  };
  return { ...history, session, past: compact(past.reverse(), session).reverse(), future: compact(future, session), lastStepKey: null, lastStepAt: 0 };
}

/** Keep the reviewed changes stable while a confirmation is open. Server
 * changes to untouched history do not invalidate the submission. */
export function isSchedulingReviewCurrent(reviewed: SchedulingSession, current: SchedulingSession): boolean {
  const fields = (p: PublicationModel) => ({
    id: p.id, projectId: p.projectId, accountId: p.accountId, provider: p.provider,
    status: p.status, scheduledAt: p.scheduledAt, postInfo: p.postInfo,
  });
  const fingerprint = (session: SchedulingSession) => {
    const changes = computeSessionPublicationChangeSet(session);
    return {
      creates: changes.publicationsToCreate.map(p => fields(p.publication)),
      updates: changes.publicationsToUpdate.map(p => [fields(p.persistedPublication), fields(p.publication)]),
      deletes: changes.publicationsToDelete.map(fields),
    };
  };
  return equal(fingerprint(reviewed), fingerprint(current));
}
